import React, { useEffect, useState, useCallback, useRef } from "react";
import { MapPin, Globe, Building2, Search, X } from "lucide-react";
import { api } from "../lib/api";

/**
 * FacilityPicker — autocomplete picker that searches the worldwide facilities
 * index by sport + free-text + (optionally) geolocation. On select, calls
 *   onChange({ facility_id, location, lat, lng })
 *
 * Props:
 *   sportSlug: string  (required to scope the search)
 *   value: { facility_id?, location } | null
 *   onChange: ({ facility_id, location, lat, lng }) => void
 *   testIdPrefix?: string (default 'facility')
 */
export default function FacilityPicker({ sportSlug, value, onChange, testIdPrefix = "facility" }) {
  const [query, setQuery] = useState(value?.location || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState(null); // {lat, lng}
  const [geoStatus, setGeoStatus] = useState("idle"); // idle | prompting | granted | denied | unavailable
  const [selectedId, setSelectedId] = useState(value?.facility_id || null);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  // Sync external value changes (e.g. parent reset) to internal state.
  useEffect(() => {
    if (value?.location !== undefined) setQuery(value.location || "");
    if (value?.facility_id !== undefined) setSelectedId(value.facility_id || null);
  }, [value?.location, value?.facility_id]);

  // Close dropdown on outside click.
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // One-time auto-prompt for geolocation per browser session — silently no-ops
  // if user already decided (granted/denied) or the browser doesn't expose it.
  const requestGeo = useCallback((force = false) => {
    if (!navigator.geolocation) { setGeoStatus("unavailable"); return; }
    const prior = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("p2p_geo_decision") : null;
    if (!force && prior) { setGeoStatus(prior); return; }
    setGeoStatus("prompting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        try { sessionStorage.setItem("p2p_geo_decision", "granted"); } catch { /* no-op */ }
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoStatus("granted");
        setOpen(true);
      },
      () => {
        try { sessionStorage.setItem("p2p_geo_decision", "denied"); } catch { /* no-op */ }
        setGeoStatus("denied");
      },
      { timeout: 5000 }
    );
  }, []);

  const search = useCallback(async (q) => {
    if (!sportSlug) { setResults([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("sport", sportSlug);
      if (q) params.set("q", q);
      if (coords) {
        params.set("lat", String(coords.lat));
        params.set("lng", String(coords.lng));
        params.set("radius_km", "50");
      }
      params.set("limit", "20");
      const { data } = await api.get(`/facilities?${params.toString()}`);
      setResults(data.results || []);
    } catch (err) {
      // Debounced search — surfacing a toast on every keystroke would be noisy.
      // Log for diagnostics; clear results so the dropdown shows the empty state.
      // eslint-disable-next-line no-console
      console.warn("[FacilityPicker] search failed:", err?.message || err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [sportSlug, coords]);

  // Debounced query change.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => search(query), 250);
    return () => clearTimeout(t);
  }, [query, open, search]);

  const useMyLocation = () => requestGeo(true);

  const onFocus = () => {
    setOpen(true);
    // Auto-prompt once per session — user can still override with the globe button.
    if (!coords && (geoStatus === "idle")) requestGeo(false);
  };

  const pick = (f) => {
    setSelectedId(f.id);
    const loc = [f.name, f.city, f.country].filter(Boolean).join(", ");
    setQuery(loc);
    setOpen(false);
    onChange?.({ facility_id: f.id, location: loc, lat: f.lat, lng: f.lng });
  };

  const clear = () => {
    setSelectedId(null);
    setQuery("");
    setOpen(false);
    onChange?.({ facility_id: null, location: "", lat: null, lng: null });
  };

  const handleManualChange = (v) => {
    // Free-text mode: clear facility selection so we don't store a stale id.
    setQuery(v);
    if (selectedId) setSelectedId(null);
    onChange?.({ facility_id: null, location: v, lat: null, lng: null });
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={`${testIdPrefix}-picker`}>
      <div className="relative">
        <Search size={14} className="absolute start-3 top-3 text-zinc-500 pointer-events-none" />
        <input
          ref={inputRef}
          data-testid={`${testIdPrefix}-input`}
          value={query}
          onChange={(e) => handleManualChange(e.target.value)}
          onFocus={onFocus}
          placeholder="Type a venue or city — or pick one below"
          className="pp-input ps-9 pe-20"
        />
        <div className="absolute end-2 top-1.5 flex items-center gap-1">
          {selectedId && (
            <span data-testid={`${testIdPrefix}-selected`} className="pp-chip pp-chip-active text-[10px] flex items-center gap-1">
              <Building2 size={10} /> Linked
            </span>
          )}
          <button type="button" onClick={useMyLocation} title="Use my location"
            data-testid={`${testIdPrefix}-geo`}
            className="p-1.5 text-zinc-400 hover:text-[#00E676]"><Globe size={14} /></button>
          {query && (
            <button type="button" onClick={clear} title="Clear"
              data-testid={`${testIdPrefix}-clear`}
              className="p-1.5 text-zinc-400 hover:text-[#FF3B30]"><X size={14} /></button>
          )}
        </div>
      </div>
      {open && (
        <div className="absolute left-0 right-0 mt-1 bg-[#131313] border border-white/10 rounded-xl shadow-xl z-30 max-h-72 overflow-auto" data-testid={`${testIdPrefix}-dropdown`}>
          {coords && (
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-[#00E676] border-b border-white/5 flex items-center gap-1" data-testid={`${testIdPrefix}-near-you`}>
              <MapPin size={10} /> Showing courts near you
            </div>
          )}
          {!coords && geoStatus === "prompting" && (
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/5 flex items-center gap-1">
              <Globe size={10} /> Asking for your location…
            </div>
          )}
          {loading && <div className="text-zinc-500 text-xs p-3">Searching{coords ? " near you" : " worldwide"}…</div>}
          {!loading && results.length === 0 && (
            <div className="text-zinc-500 text-xs p-3" data-testid={`${testIdPrefix}-empty`}>
              No matches yet — try typing a city or click <Globe size={11} className="inline mx-0.5" /> to use your location.
            </div>
          )}
          {!loading && results.map((f) => (
            <button key={f.id} type="button" onClick={() => pick(f)}
              data-testid={`${testIdPrefix}-option-${f.id}`}
              className="w-full text-start p-3 hover:bg-white/5 border-b border-white/5 last:border-b-0 flex items-start gap-2">
              <MapPin size={14} className="mt-0.5 text-[#00E676] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{f.name}</div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {[f.city, f.country].filter(Boolean).join(" · ")}
                  {f.indoor !== null && f.indoor !== undefined && (
                    <span className="ms-2 pp-chip text-[9px]">{f.indoor ? "Indoor" : "Outdoor"}</span>
                  )}
                  {f.num_courts ? <span className="ms-1 text-zinc-600">· {f.num_courts} court{f.num_courts === 1 ? "" : "s"}</span> : null}
                </div>
              </div>
              <span className={`text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full ${f.source === "seed" ? "bg-[#007AFF]/15 text-[#007AFF]" : f.source === "osm" ? "bg-[#FFD60A]/15 text-[#FFD60A]" : "bg-white/5 text-zinc-400"}`}>{f.source}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
